import * as Sentry from "@sentry/react";

/** PII patterns to strip from breadcrumbs and events */
const SENSITIVE_KEYS = new Set([
  'password', 'oldpassword', 'newpassword', 'confirmpassword',
  'token', 'accesstoken', 'access_token', 'refreshtoken', 'refresh_token',
  'authorization', 'cookie', 'set-cookie',
  'x-api-key', 'apikey', 'api_key',
  'secret', 'secretkey', 'secret_key',
  'jwt', 'jwttoken', 'jwt_token',
  'creditcard', 'credit_card', 'ssn',
  'stripe_secret', 'sk_live', 'sk_test',
]);

/** Test if a key holds sensitive data */
const isSensitiveKey = (key) =>
  SENSITIVE_KEYS.has(key?.toLowerCase?.()) ||
  /secret|token|password|credential|apikey|api_key/i.test(key);

/**
 * Recursively redact sensitive fields from an object
 */
function redactSensitive(obj, depth = 0) {
  if (!obj || typeof obj !== 'object' || depth > 5) return obj;
  const cleaned = Array.isArray(obj) ? [...obj] : { ...obj };
  for (const key of Object.keys(cleaned)) {
    if (isSensitiveKey(key)) {
      cleaned[key] = '[REDACTED]';
    } else if (typeof cleaned[key] === 'object') {
      cleaned[key] = redactSensitive(cleaned[key], depth + 1);
    }
  }
  return cleaned;
}

/**
 * Initialize Sentry for React application
 * @param {object} [options] - Overrides for Sentry.init
 */
export function initializeSentry(options = {}) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.MODE || 'development';
  const release = import.meta.env.VITE_APP_VERSION || import.meta.env.VITE_GIT_SHA || undefined;

  // Only initialize if DSN is provided
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.log('[Sentry] DSN not configured — error tracking disabled');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // Do not send cookies, user IP, or logged-in user info
    sendDefaultPii: false,

    // Performance sampling — lower in production to reduce cost
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session Replay
    replaysSessionSampleRate: environment === 'production' ? 0.05 : 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Max breadcrumbs
    maxBreadcrumbs: 50,

    integrations: [
      // Automatically instrument browser navigation
      Sentry.browserTracingIntegration(),

      // Session Replay
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        // Mask all inputs by default (PII safety)
        maskAllInputs: true,
      }),
    ],

    // ─── Event scrubbing ────────────────────────────────────────
    beforeSend(event, _hint) {
      // Redact breadcrumb data
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            breadcrumb.data = redactSensitive(breadcrumb.data);
          }
          return breadcrumb;
        });
      }

      // Redact request headers
      if (event.request?.headers) {
        event.request.headers = redactSensitive(event.request.headers);
      }

      // Redact request body
      if (event.request?.data) {
        try {
          const parsed = typeof event.request.data === 'string'
            ? JSON.parse(event.request.data)
            : event.request.data;
          event.request.data = JSON.stringify(redactSensitive(parsed));
        } catch (_e) { /* not JSON — leave as-is */ }
      }

      // Strip query-string tokens
      if (event.request?.query_string) {
        event.request.query_string = event.request.query_string
          .replace(/token=[^&]*/gi, 'token=[REDACTED]')
          .replace(/key=[^&]*/gi, 'key=[REDACTED]');
      }

      return event;
    },

    // ─── Breadcrumb filtering ───────────────────────────────────
    beforeBreadcrumb(breadcrumb) {
      // Suppress noisy console breadcrumbs in development
      if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
        return null;
      }
      // Suppress overly frequent XHR breadcrumbs (polling health checks)
      if (breadcrumb.category === 'xhr' && breadcrumb.data?.url?.includes('/health')) {
        return null;
      }
      return breadcrumb;
    },

    // ─── Ignore list ────────────────────────────────────────────
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      /chrome-extension:\/\//,
      /moz-extension:\/\//,
      // Network errors (transient)
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      'Load failed',
      // Aborted requests
      'AbortError',
      'The operation was aborted',
      // ResizeObserver noise (benign)
      'ResizeObserver loop',
      // Non-error promise rejections
      'Non-Error promise rejection captured',
    ],

    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Third-party scripts
      /^https?:\/\/www\.google-analytics\.com/,
      /^https?:\/\/cdn\.segment\.com/,
    ],

    // Allow caller overrides
    ...options,
  });

  if (import.meta.env.DEV) {
    console.log(`[Sentry] Initialized for ${environment}${release ? ` (${release})` : ''}`);
  }
}

// ─── Context helpers ────────────────────────────────────────────

/**
 * Capture an exception with additional context
 */
export function captureException(error, context = {}) {
  Sentry.captureException(error, {
    extra: redactSensitive(context),
  });
}

/**
 * Capture a message with level
 * @param {string} message
 * @param {'fatal'|'error'|'warning'|'info'|'debug'} level
 */
export function captureMessage(message, level = 'info') {
  Sentry.captureMessage(message, level);
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(breadcrumb) {
  Sentry.addBreadcrumb(breadcrumb);
}

/**
 * Set user context for error tracking
 */
export function setUser(user) {
  if (user) {
    Sentry.setUser({
      id: user._id || user.id,
      email: user.email,
      username: user.name,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Set organization context (for multi-tenant debugging)
 */
export function setOrganization(org) {
  if (org) {
    Sentry.setTag('organization.id', org._id || org.id);
    Sentry.setTag('organization.name', org.name);
    Sentry.setTag('organization.tier', org.subscription?.tier || 'free');
  }
}

/**
 * Set a custom tag
 */
export function setTag(key, value) {
  Sentry.setTag(key, value);
}

/**
 * Start a manual performance transaction
 * @param {string} name - Transaction name
 * @param {string} op - Operation name (e.g., 'ui.action', 'api.call')
 * @returns {object} span handle with `.end()` method
 */
export function startTransaction(name, op = 'custom') {
  return Sentry.startInactiveSpan({ name, op });
}

/**
 * Wrap async work with error boundary and Sentry reporting
 * @param {string} label - Context name for Sentry
 * @param {Function} fn - Async function
 */
export async function withSentryScope(label, fn) {
  return Sentry.withScope(async (scope) => {
    scope.setTag('operation', label);
    try {
      return await fn();
    } catch (error) {
      captureException(error, { operation: label });
      throw error;
    }
  });
}

/**
 * Flush pending events (call before unload or on critical error)
 */
export async function flush(timeoutMs = 2000) {
  await Sentry.flush(timeoutMs);
}

export { Sentry };
